import { RouteSkeleton, type RouteSkeletonLayout } from '@laura/design-system';
import { lazyPage, LazyRoute, type Loader } from '@laura/portal-kit';
import type { ComponentType, ReactNode } from 'react';
import { STAFF_MODULES } from './nav.js';

/**
 * Route-level code splitting for the back office (plan 45 phase 2): each
 * module is its own chunk, preloaded when its link is hovered or focused;
 * Clinics, the landing page, is preloaded after the first paint.
 */
const backoffice = () => import('./backoffice.js');
const governance = () => import('./governance.js');

const MODULES: Record<string, { component: ComponentType; layout: RouteSkeletonLayout }> = {
  clinics: {
    component: lazyPage(
      () => import('./clinics.js'),
      (m) => m.ClinicsRoutes,
    ),
    layout: 'table',
  },
  agents: {
    component: lazyPage(
      () => import('./agent.js'),
      (m) => m.AgentLifecyclePage,
    ),
    layout: 'list',
  },
  templates: {
    component: lazyPage(
      () => import('./templates.js'),
      (m) => m.TemplatesRoutes,
    ),
    layout: 'list',
  },
  review: {
    component: lazyPage(
      () => import('./review.js'),
      (m) => m.ReviewPage,
    ),
    layout: 'list',
  },
  // ADR 210 §6: Conversation quality › Scenario stability.
  quality: {
    component: lazyPage(
      () => import('./quality.js'),
      (m) => m.QualityPage,
    ),
    layout: 'table',
  },
  monitoring: {
    component: lazyPage(
      () => import('./monitoring.js'),
      (m) => m.MonitoringRoutes,
    ),
    layout: 'table',
  },
  finance: {
    component: lazyPage(
      () => import('./finance.js'),
      (m) => m.FinancePage,
    ),
    layout: 'dashboard',
  },
  health: {
    component: lazyPage(
      () => import('./health.js'),
      (m) => m.HealthRoutes,
    ),
    layout: 'dashboard',
  },
  support: {
    component: lazyPage(
      () => import('./access.js'),
      (m) => m.SupportPage,
    ),
    layout: 'detail',
  },
  'support-requests': {
    component: lazyPage(
      () => import('./support-requests.js'),
      (m) => m.SupportRequestsPage,
    ),
    layout: 'list',
  },
  billing: { component: lazyPage(backoffice, (m) => m.BillingPage), layout: 'table' },
  staff: { component: lazyPage(backoffice, (m) => m.StaffPage), layout: 'table' },
  audit: { component: lazyPage(backoffice, (m) => m.AuditPage), layout: 'table' },
  cost: { component: lazyPage(backoffice, (m) => m.CostPage), layout: 'table' },
  flags: { component: lazyPage(backoffice, (m) => m.FlagsPage), layout: 'table' },
  privacy: { component: lazyPage(backoffice, (m) => m.PrivacyPage), layout: 'table' },
  approvals: { component: lazyPage(governance, (m) => m.ApprovalsPage), layout: 'list' },
  incidents: { component: lazyPage(governance, (m) => m.IncidentsPage), layout: 'list' },
  'ops-incidents': {
    component: lazyPage(
      () => import('./ops-incidents.js'),
      (m) => m.OpsIncidentsPage,
    ),
    layout: 'table',
  },
  terms: { component: lazyPage(governance, (m) => m.TermsPage), layout: 'list' },
  'safety-terms': {
    component: lazyPage(
      () => import('./safety-terms.js'),
      (m) => m.SafetyTermsPage,
    ),
    layout: 'table',
  },
  'test-recipients': {
    component: lazyPage(
      () => import('./test-recipients.js'),
      (m) => m.TestRecipientsPage,
    ),
    layout: 'table',
  },
  numbers: {
    component: lazyPage(
      () => import('./numbers.js'),
      (m) => m.NumbersPage,
    ),
    layout: 'table',
  },
  voice: {
    component: lazyPage(
      () => import('./voice-settings.js'),
      (m) => m.VoiceSettingsPage,
    ),
    layout: 'detail',
  },
  'partner-feed': {
    component: lazyPage(
      () => import('./partner-feed.js'),
      (m) => m.PartnerFeedPage,
    ),
    layout: 'list',
  },
};

function label(id: string): string {
  return STAFF_MODULES.find((m) => m.id === id)?.label ?? 'Back office';
}

function Lazy({ id, children }: { id: string; children: ReactNode }) {
  const layout = MODULES[id]?.layout ?? 'detail';
  return (
    <LazyRoute product="Novations" fallback={<RouteSkeleton title={label(id)} layout={layout} />}>
      {children}
    </LazyRoute>
  );
}

/** The module pages for `App`, each in its own chunk and Suspense boundary. */
export function lazyPages(): Record<string, ReactNode> {
  return Object.fromEntries(
    Object.entries(MODULES).map(([id, { component: Component }]) => [
      id,
      <Lazy key={id} id={id}>
        <Component />
      </Lazy>,
    ]),
  );
}

/** The chunk behind a sidebar link, for preloading on hover and focus. */
export function preloadFor(href: string): Loader | undefined {
  const id = STAFF_MODULES.find((m) => href.startsWith(m.path))?.id;
  const component = id ? MODULES[id]?.component : undefined;
  return (component as { preload?: Loader } | undefined)?.preload;
}

export const PRELOAD_AFTER_PAINT: Loader[] = [preloadFor('/clinics')].filter(
  (l): l is Loader => l !== undefined,
);
